import { Badge, Box, Button, Card, Code, Group, List, SegmentedControl, Text, Textarea, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import getErrorMessage from '../../../helpers/getErrorMessage';
import { DIFF_LINE_STYLES, diffLines } from '../../../helpers/lineDiff';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import HelpPopover from '../../HelpPopover/HelpPopover';
import { formatDate } from '../../KnowledgeBase/shared';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import Spinner from '../../Spinner';
import { HELP } from './superAdminHelp';

// The words the rest of the block uses for the versioned fields; an unknown field shows its own name.
const FIELD_LABELS = {
  agentPlaybook: 'Playbook',
  agentFieldMapping: 'Field mapping',
  priceDisplayRule: 'Hide price when',
  agentUi: 'Agent UI',
  profileVersion: 'Profile version',
  provider: 'Provider',
  model: 'Model',
  orderStatusEnabled: 'Order status lookup',
  supportEmail: 'Support email',
  supportMessage: 'Support message',
  documentsNumber: 'Documents in Store section',
  productsNumber: 'Products in Store section',
  multiLocaleProducts: 'Multi-locale products',
  initialMessages: 'Initial messages',
};

const fieldLabel = (field) => FIELD_LABELS[field] ?? field;

const LEARNING_KINDS = { ai: 'AI suggestion', correction: 'AI-rewritten correction', promote: 'Promoted answer' };

// Who made a version and from where, in words.
const sourceWords = ({ source, meta, user }) => {
  if (meta?.baseline) return 'Baseline — state before history started';
  const name = user ? user.name || user.email : 'a deleted user';
  if (source === 'learning') {
    if (meta?.edited) return `Learning · edited by ${name}`;
    return `${LEARNING_KINDS[meta?.kind] ?? 'Learning'} · approved by ${name}`;
  }
  if (source === 'restore') return `Restored v${meta?.restoredFrom} · ${name}`;
  if (source === 'manual') return `Manual · ${name}`;
  return 'System';
};

// JSONB does not keep key order, so values compare with sorted keys; a missing value is null.
const canonical = (value) =>
  JSON.stringify(value ?? null, (key, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v)
            .sort()
            .map((k) => [k, v[k]])
        )
      : v
  );

const changedFields = (from, to) =>
  [...new Set([...Object.keys(to), ...Object.keys(from)])].filter(
    (field) => canonical(from[field]) !== canonical(to[field])
  );

const asText = (field, value) =>
  field === 'agentPlaybook' ? value || '' : value == null ? '' : JSON.stringify(value, null, 2);

const DIFF_BOX = {
  padding: '12px 14px',
  background: 'var(--mantine-color-gray-0)',
  borderBottom: '1px solid var(--mantine-color-gray-1)',
  fontFamily: 'var(--mantine-font-family-monospace)',
  fontSize: 12,
  lineHeight: 1.8,
};

const ROW = { padding: '10px 12px', borderBottom: '1px solid var(--mantine-color-gray-1)' };

const FieldDiffs = ({ from, to }) => {
  const fields = changedFields(from, to);
  if (fields.length === 0) {
    return (
      <Text fz={12} c="dimmed">
        No differences.
      </Text>
    );
  }
  return fields.map((field) => (
    <Box key={field} mb={8}>
      <Text fz={11} fw={600} c="dimmed">
        {fieldLabel(field)}
      </Text>
      {diffLines(asText(field, from[field]), asText(field, to[field])).map((line, i) => (
        <div key={i} style={DIFF_LINE_STYLES[line.kind]}>
          {line.text || ' '}
        </div>
      ))}
    </Box>
  ));
};

// Previous → this version by default (this → current when nothing earlier is kept), plus the full playbook.
const DiffPanel = ({ revision, previous, current }) => {
  const [mode, setMode] = useState(previous ? 'previous' : 'current');
  const [showFull, setShowFull] = useState(false);
  const from = mode === 'previous' ? previous.snapshot : revision.snapshot;
  const to = mode === 'previous' ? revision.snapshot : current.snapshot;

  return (
    <div style={DIFF_BOX}>
      <Group gap="xs" mb={8}>
        <SegmentedControl
          size="xs"
          aria-label="Compare"
          value={mode}
          onChange={setMode}
          data={[
            {
              value: 'previous',
              label: previous ? `v${previous.version} → v${revision.version}` : 'No earlier version',
              disabled: !previous,
            },
            { value: 'current', label: `v${revision.version} → current` },
          ]}
        />
        <Button variant="subtle" size="compact-xs" onClick={() => setShowFull((shown) => !shown)}>
          {showFull ? 'Hide full playbook' : 'Show full playbook'}
        </Button>
      </Group>
      {showFull ? (
        <Code block data-testid="full-playbook">
          {revision.snapshot.agentPlaybook || '(empty)'}
        </Code>
      ) : (
        <FieldDiffs from={from} to={to} />
      )}
    </div>
  );
};

const RestoreButton = ({ blocked, children, onClick }) => (
  <Tooltip label="Save or discard your changes first" disabled={!blocked} withArrow>
    <span>
      <Button size="xs" variant="default" disabled={blocked} onClick={onClick}>
        {children}
      </Button>
    </span>
  </Tooltip>
);

/**
 * Every kept version of the playbook and the agent settings, newest first, with a diff and a restore.
 * A restore is a new version written by the API; the page then takes the returned chat bot in as loaded.
 */
const HistorySection = ({ chatBot, fetchUrl, chatBotChanged, refreshKey, onChatBotRestored, onChatBotReload }) => {
  const fetch = useFetch();
  const base = `${fetchUrl}/chatBot/revisions`;

  const [history, setHistory] = useState();
  const [loadError, setLoadError] = useState();
  const [reloads, setReloads] = useState(0);
  const details = useRef({});
  const [open, setOpen] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [restore, setRestore] = useState(null);
  const [note, setNote] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState();

  useEffect(() => {
    let cancelled = false;
    fetch(base)
      .then((data) => {
        if (cancelled) return;
        setHistory(data);
        setLoadError(undefined);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, base, reloads, refreshKey]);

  const reload = () => {
    setOpen(null);
    setReloads((count) => count + 1);
  };

  // Snapshots never change, so each version is fetched once.
  const loadDetail = useCallback(
    (id) => {
      details.current[id] ??= fetch(`${base}/${id}`).catch((error) => {
        delete details.current[id];
        throw error;
      });
      return details.current[id];
    },
    [fetch, base]
  );

  // A version with the newest one (the current state) beside it.
  const withCurrent = (revision) => {
    setLoadingId(revision.id);
    return Promise.all([loadDetail(revision.id), loadDetail(history.revisions[0].id)])
      .catch((error) => {
        notifications.show({ title: 'Failed to load version', message: getErrorMessage(error), color: 'danger' });
        return null;
      })
      .finally(() => setLoadingId(null));
  };

  const toggleDiff = (revision) => {
    if (open?.revision.id === revision.id) {
      setOpen(null);
      return;
    }
    withCurrent(revision).then((loaded) => {
      if (!loaded) return;
      const [{ revision: full, previous }, { revision: current }] = loaded;
      setOpen({ revision: full, previous, current });
    });
  };

  const askRestore = (revision, scope) => {
    withCurrent(revision).then((loaded) => {
      if (!loaded) return;
      const [{ revision: full }, { revision: current }] = loaded;
      const fields = changedFields(current.snapshot, full.snapshot);
      setRestore({
        revision,
        scope,
        fields: scope === 'playbook' ? fields.filter((f) => f === 'agentPlaybook') : fields,
      });
    });
  };

  const closeRestore = () => {
    setRestore(null);
    setNote('');
    setRestoreError(undefined);
  };

  const confirmRestore = (close) => {
    const { revision, scope } = restore;
    setRestoring(true);
    setRestoreError(undefined);
    fetch(`${base}/${revision.id}/restore`, {
      method: 'POST',
      body: JSON.stringify({
        scope,
        agentPlaybookHash: chatBot.agentPlaybookHash,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    })
      .then((response) => {
        close();
        if (response.unchanged) {
          notifications.show({ message: `v${revision.version} matches the current settings; nothing changed.` });
          return;
        }
        onChatBotRestored?.(response.chatBot);
        notifications.show({
          message: `Restored v${revision.version} as v${response.revision.version}`,
          color: 'success',
        });
        const stale = response.stalePending || 0;
        if (stale > 0) {
          notifications.show({
            color: 'warning',
            message: `${stale} pending playbook item${stale === 1 ? ' was' : 's were'} drafted on the previous playbook — edit them onto this one in Learning before approving.`,
          });
        }
        // The page bumps refreshKey when it takes the restored chat bot in, which reloads the list.
        setOpen(null);
      })
      .catch((error) => {
        // The playbook changed since the page loaded: take in the latest and say so.
        if (error?.agentPlaybookHash) {
          close();
          notifications.show({ title: 'Restore failed', message: getErrorMessage(error), color: 'danger' });
          onChatBotReload?.();
          reload();
          return;
        }
        setRestoreError({ message: getErrorMessage(error) });
      })
      .finally(() => setRestoring(false));
  };

  if (loadError) return <ErrorMessage message={getErrorMessage(loadError)} />;
  if (!history) return <Spinner />;

  const { revisions } = history;

  return (
    <>
      <Group gap={4} mb="xs">
        <Text size="sm" fw={500}>
          Playbook & agent settings history
        </Text>
        <HelpPopover {...HELP.history} />
      </Group>

      <Card withBorder radius="sm" padding={0}>
        {revisions.length === 0 && (
          <Text size="sm" c="dimmed" p="sm">
            No history yet — it starts with the next save that changes the playbook or an agent setting.
          </Text>
        )}
        {revisions.map((revision, index) => {
          const baseline = revision.meta?.baseline === true;
          return (
            <Fragment key={revision.id}>
              <Group gap={10} wrap="wrap" style={ROW} data-testid="revision-row">
                <Badge variant="default" size="sm" tt="none">
                  v{revision.version}
                </Badge>
                <Box style={{ flex: 1, minWidth: 220 }}>
                  <Group gap={8}>
                    <Text size="sm" fw={500}>
                      {sourceWords(revision)}
                    </Text>
                    {index === 0 && (
                      <Badge variant="light" color="info" size="sm">
                        Current
                      </Badge>
                    )}
                    {!baseline && (
                      <Text fz={12} c="dimmed">
                        {formatDate(revision.createdAt)}
                      </Text>
                    )}
                  </Group>
                  {revision.meta?.title && (
                    <Text fz={12} c="dimmed">
                      {revision.meta.title}
                    </Text>
                  )}
                  {!baseline && revision.changedFields.length > 0 && (
                    <Group gap={4} mt={4}>
                      {revision.changedFields.map((field) => (
                        <Badge key={field} variant="light" size="xs" tt="none">
                          {fieldLabel(field)}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  {revision.note && (
                    <Text fz={12} c="dimmed" fs="italic" mt={2}>
                      {revision.note}
                    </Text>
                  )}
                </Box>
                <Button
                  size="xs"
                  variant="default"
                  loading={loadingId === revision.id}
                  onClick={() => toggleDiff(revision)}
                >
                  {open?.revision.id === revision.id ? 'Hide diff' : 'View diff'}
                </Button>
                {index > 0 && (
                  <>
                    <RestoreButton blocked={chatBotChanged} onClick={() => askRestore(revision, 'playbook')}>
                      Restore playbook
                    </RestoreButton>
                    <RestoreButton blocked={chatBotChanged} onClick={() => askRestore(revision, 'config')}>
                      Restore all agent settings
                    </RestoreButton>
                  </>
                )}
              </Group>
              {open?.revision.id === revision.id && (
                <DiffPanel revision={open.revision} previous={open.previous} current={open.current} />
              )}
            </Fragment>
          );
        })}
      </Card>

      <ConfirmationModal
        show={restore !== null}
        close={closeRestore}
        confirm={confirmRestore}
        loading={restoring}
        error={restoreError}
        message={
          restore &&
          `This writes ${restore.scope === 'playbook' ? 'the playbook' : 'the agent settings'} of v${restore.revision.version} as a new version.`
        }
      >
        {restore && (
          <>
            {restore.fields.length > 0 ? (
              <>
                <Text size="sm">Will change:</Text>
                <List size="sm" mb="xs">
                  {restore.fields.map((field) => (
                    <List.Item key={field}>{fieldLabel(field)}</List.Item>
                  ))}
                </List>
              </>
            ) : (
              <Text size="sm" mb="xs">
                Nothing differs from the current version.
              </Text>
            )}
            {restore.fields.includes('agentPlaybook') && (
              <Text size="xs" c="warning.9" mb={4}>
                Pending playbook items in Learning were drafted on the current playbook and may need editing onto the
                restored one.
              </Text>
            )}
            <Text size="xs" c="dimmed" mb="xs">
              Knowledge documents added by Learning stay.
            </Text>
            <Textarea
              label="Note"
              description="Optional."
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              autosize
              minRows={2}
            />
          </>
        )}
      </ConfirmationModal>
    </>
  );
};

export default HistorySection;
